import { DefaultError, queryOptions, useQuery } from '@tanstack/react-query';

import { KentWorkload } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const KENT_REBALANCE_WORKLOADS_QUERY_KEY = 'kent-rebalance-workloads';

const getKentRebalanceWorkloadsQueryKey = (clusterId?: string) =>
  [KENT_REBALANCE_WORKLOADS_QUERY_KEY, clusterId] as const;

const getKentRebalanceWorkloadsQueryOptions = ({
  organizationId,
  clusterId,
  enabled = true,
  refetchInterval,
}: {
  organizationId?: string;
  clusterId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}) =>
  queryOptions({
    queryKey: getKentRebalanceWorkloadsQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.kent.getWorkloads(
        {
          organizationId: organizationId ?? '',
          clusterId: clusterId ?? '',
        },
        {
          timeout: 60_000,
        }
      );
      return data.items;
    },
    enabled: enabled && !!clusterId && !!organizationId,
    refetchInterval,
  });

type UseKentRebalanceWorkloadsQueryArgs = {
  clusterId?: string;
  enabled?: boolean;
  refetchInterval?: number | false;
};

type UseKentRebalanceWorkloadsQuery = {
  isLoading: boolean;
  workloads?: KentWorkload[];
  error?: DefaultError;
  refetch: () => void;
};

export const useKentRebalanceWorkloadsQuery = ({
  clusterId,
  enabled = true,
  refetchInterval,
}: UseKentRebalanceWorkloadsQueryArgs): UseKentRebalanceWorkloadsQuery => {
  const { currentOrganization } = useOrganizations();

  const {
    isLoading,
    data: workloads,
    error,
    refetch,
  } = useQuery(
    getKentRebalanceWorkloadsQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      enabled,
      refetchInterval,
    })
  );

  return {
    isLoading,
    workloads: workloads as KentWorkload[],
    error: error || undefined,
    refetch,
  };
};
